import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/reducers';
import { ToastService } from 'src/app/services/toast.service';
import { Beneficio, BeneficioCategoria, ContenidoStatus } from 'src/common/contenido';
import { ContenidoService } from '../../services/contenido.service';
import { BeneficioActionTypes, BeneficioCategoriaChangeCancelled, BeneficioCategoriaChangeLoaded, BeneficioCategoriaChangeRequested, BeneficioCategoriasCancelled, BeneficioCategoriasLoaded, BeneficioCategoriasRequested, BeneficioCategoriasRight, BeneficioPageCancelled, BeneficioPageLoaded, BeneficioPageRequested, BeneficioPageRight } from './beneficio.actions';
import { beneficioCategoriaSelected, beneficioCategorias, beneficioNextPageNum, beneficioPageSize, beneficioStatus } from './beneficio.selectors';

@Injectable()
export class BeneficioEffects {

	loadBeneficio$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<BeneficioPageRequested>( BeneficioActionTypes.BeneficioPageRequested ),
			withLatestFrom( this.store.select( beneficioCategoriaSelected ) ),
			switchMap( ([ action, categoria ]) => {
				return this.service.findBeneficioStatus( categoria ).pipe(
						map( remoteStatus => {
							return { action, categoria, remoteStatus };
						}),
						catchError( error => of( { action, categoria, remoteStatus: null } ) )
					);
			}),
			withLatestFrom( this.store.select( beneficioStatus ), this.store.select( beneficioNextPageNum ), this.store.select( beneficioPageSize ) ),
			switchMap( ([ data, localStatus, nextPageNum, pageSize ]) => {
				let resultAction: Observable<Action>;
				let shouldLoadMoreContent: boolean = data["action"]["payload"]["shouldLoadMoreContent"];
				let remoteStatus: ContenidoStatus = data["remoteStatus"];
				let categoria: BeneficioCategoria = data["categoria"];
				let cleared: boolean = false;

				if( remoteStatus == null ) {
					resultAction = of( new BeneficioPageCancelled( { status: null } ) );
					this.toastService.danger( "No se pudo obtener el estado de los beneficios" );
				} else {
					// Hay actualizaciones nuevas?
					if( 
						( localStatus && localStatus.lastUpdate && localStatus.qty >= 0 ) &&
						( localStatus.lastUpdate.getTime() != remoteStatus.lastUpdate.getTime() || localStatus.qty != remoteStatus.qty )
					 ) {
						nextPageNum = 0;
						cleared = true;
					}

					// Se carga el componente por primera vez?
					if( !localStatus )
						shouldLoadMoreContent = true;

					if( remoteStatus.qty == 0 ) {
						resultAction = of( new BeneficioPageLoaded( { cleared, status: remoteStatus, nextPageNum, beneficio: [] } ) );
					} else if( cleared || shouldLoadMoreContent ) {
						resultAction = this.service.findBeneficioByPage( categoria, { pageNum: nextPageNum, pageSize } )
							.pipe(
								map( beneficio => {
									let action: Action;
									if( beneficio.length == 0 ) {
										action = new BeneficioPageCancelled( { status: localStatus } );
									} else {
										++nextPageNum;
										action = new BeneficioPageLoaded( { cleared, status: remoteStatus, nextPageNum, beneficio } );
									}
									return action;
								}),
								catchError( error => {
									// Si no pude recuperar los nuevos beneficios, me quedo con el status local, no actualizo al nuevo (remoteStatus)
									// El status y los beneficos del store deben estar sincronizados
									this.toastService.danger( "No se pudieron obtener los beneficios" );
									return of( new BeneficioPageCancelled( { status: localStatus } ) );
								})
							);

					} else {
						// No hubieron cambios en los beneficios
						resultAction = of( new BeneficioPageRight() );
					}	
				}

				return resultAction;
			})
		);
	});

	loadBeneficioCategorias$ = createEffect( () => {
		return this.actions$.pipe(
			ofType<BeneficioCategoriasRequested>( BeneficioActionTypes.BeneficioCategoriasRequested ),
			withLatestFrom( this.store.select( beneficioCategorias ) ),
			switchMap( ([ action, categorias ]) => {
				let resultAction: Observable<Action>;

				if( !categorias ) {
					resultAction = this.service.findAllBeneficioCategorias().pipe(
						map( categorias => {
							return categorias == null || categorias.length == 0 ? 
								new BeneficioCategoriasCancelled() :
								new BeneficioCategoriasLoaded( { categorias } );
						}),
						catchError( error => {
							this.toastService.danger( "No se pudieron obtener las categorias de los beneficios" );
							return of( new BeneficioCategoriasCancelled() );
						})
					);
				} else {
					resultAction = of( new BeneficioCategoriasRight() );
				}

				return resultAction;
			})
		);
	});

	beneficioCategoriaChangedRequested = createEffect( () => {
		return this.actions$
		.pipe(
			ofType<BeneficioCategoriaChangeRequested>( BeneficioActionTypes.BeneficioCategoriaChangeRequested ),
			withLatestFrom( this.store.select( beneficioCategoriaSelected ) ),
			map( ([ action, selectedInStore ]) => {
				let categoria: BeneficioCategoria = action["payload"]["categoria"];
				return categoria.tipo == selectedInStore.tipo? new BeneficioCategoriaChangeCancelled(): new BeneficioCategoriaChangeLoaded( { categoria } );
			})
		);
	});

	beneficioCategoriaChangedLoaded = createEffect( () => {
		return this.actions$.pipe(
			ofType<BeneficioCategoriaChangeLoaded>( BeneficioActionTypes.BeneficioCategoriaChangeLoaded ),
			map( () => new BeneficioPageRequested( { shouldLoadMoreContent: true } ) )
		);
	});


	constructor( private actions$: Actions, private store: Store<AppState>, private service: ContenidoService, private toastService: ToastService ) {
	}

}